import { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { Link, useSearchParams } from "react-router";
import { Button } from "@/components/design/Button";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { Section } from "@/components/design/Section";
import {
  EMPTY_TITLE_FILTERS,
  fetchFilterOptions,
  fetchTitles,
  TobisNotConfiguredError,
  type FilterOptions,
  type KeywordMatch,
  type TobisTitleFilters,
  type TobisTitleSort,
} from "@/lib/tobis";
import { logApplicationFailure } from "@/lib/user-error";
import { useTobisRegisterStore } from "@/stores/tobis-register-store";
import { TobisPageShell } from "./TobisPageShell";
import { TobisTitleFilterBar } from "./TobisFilters";
import { TobisTitleCard } from "./TobisTitleCard";
import { TobisTitleRow } from "./TobisTitleRow";

type TobisView = "grid" | "compact";

// Register der Titel. Zeigt die modellierte Sicht (model.title), in der die
// Formatfallen der Quellen schon behandelt sind, die Seite parst nichts selbst.

export function TobisTitlesPage() {
  const intl = useIntl();
  // Filter, Sortierung und die geladene Seitenfolge liegen im Store, damit sie
  // ein Zurueck von einer Titelseite ueberleben (siehe tobis-register-store).
  const {
    filters,
    sort,
    keywords,
    keywordMatchAll,
    titles,
    cursor,
    total,
    loaded,
    setFilters,
    setSort,
    setKeywords,
    setKeywordMatchAll,
    setPage,
    appendPage,
    markLoaded,
  } = useTobisRegisterStore();
  const [options, setOptions] = useState<FilterOptions | null>(null);
  const [phase, setPhase] = useState<"loading" | "ready" | "notConfigured" | "error">(
    loaded ? "ready" : "loading",
  );
  const [loadingMore, setLoadingMore] = useState(false);
  // Nur mit Titelbild ist der einzige Default-Filter, der 0 Treffer plausibel
  // ganz allein erklaeren kann (siehe EMPTY_TITLE_FILTERS.hasImage=true) - bei
  // 0 Treffern deshalb gezielt gegenchecken, ob es ohne ihn welche gibt, statt
  // pauschal "keine Daten geladen" zu zeigen (das war fuer z.B. den
  // DFFF-Quellenfilter foermlich falsch: 1.691 Titel, aber keiner mit Cover).
  const [hasResultsWithoutImage, setHasResultsWithoutImage] = useState(false);
  // Reine Anzeigepraeferenz, keine Bestandsdaten - bewusst lokaler State statt
  // im Store (der haelt Filter/Sortierung/Seiten, siehe tobis-register-store).
  const [view, setView] = useState<TobisView>("grid");

  // ?search= ist der Eingang von aussen (Titel-Link aus einer Filmografie) und
  // hat Vorrang vor dem gemerkten Stand - wer mit einem Suchbegriff herkommt,
  // will danach suchen und nicht die letzte Liste sehen.
  const [params] = useSearchParams();
  const searchParam = params.get("search");
  useEffect(() => {
    if (searchParam !== null && searchParam !== filters.search) {
      setFilters({ ...EMPTY_TITLE_FILTERS, search: searchParam });
    }
    // Absichtlich nur an searchParam gehaengt: reagiert die Wirkung auch auf
    // filters.search, ueberschreibt sie jede spaetere Eingabe im Suchfeld.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam]);

  // Zaehlt die Ladeauftraege durchgaengig hoch. Das Debounce unten cancelt nur
  // den Timer vor einem Request, nicht den Request selbst - tippt man schnell
  // weiter (oder der erste Aufruf beim Mounten ist noch unterwegs), kommt eine
  // AELTERE Antwort manchmal NACH einer neueren zurueck und wuerde ohne diesen
  // Vergleich das frische Suchergebnis mit einem veralteten ueberschreiben.
  const latestRequestRef = useRef(0);

  const load = useCallback(
    async (
      active: TobisTitleFilters,
      activeSort: TobisTitleSort,
      activeKeywords: KeywordMatch[],
      activeKeywordMatchAll: boolean,
    ) => {
      const requestId = ++latestRequestRef.current;
      // Sofort auf "loading" umschalten, nicht erst wenn die Antwort da ist:
      // setFilters/setSort/setKeywords leeren `titles` im Store schon beim
      // Tippen (siehe tobis-register-store), phase stand bis hierher noch auf
      // dem alten "ready" - das liess jede laufende Suche wie 0 Treffer
      // aussehen statt wie eine laufende Abfrage.
      setPhase("loading");
      setHasResultsWithoutImage(false);
      try {
        // Mit Keywords rangiert der BFF die volle Kandidatenmenge nach
        // Tonalitaets-Ueberlappung statt zu blaettern (siehe fetchTitles) - die
        // Antwort hat dann nie einen Cursor, "Load more" verschwindet
        // automatisch ueber page.pageInfo.hasNextPage.
        const page = await fetchTitles(
          active,
          100,
          null,
          activeSort,
          activeKeywords.map((k) => k.keywordId),
          undefined,
          activeKeywordMatchAll,
        );
        if (requestId !== latestRequestRef.current) return;
        setPage(page.titles, page.pageInfo.hasNextPage ? page.pageInfo.endCursor : null, page.total);
        setPhase("ready");
        if (page.titles.length === 0 && active.hasImage) {
          // Gezielter Gegencheck nur fuer diesen einen Filter (Default an) -
          // andere Kombinationen (Genre+Jahr etc.) haben keine derart
          // verlaessliche "das ist wahrscheinlich der Grund"-Heuristik.
          // limit=1: reine Existenzpruefung, kein echter Count noetig.
          try {
            const probe = await fetchTitles({ ...active, hasImage: false }, 1, null, activeSort);
            if (requestId !== latestRequestRef.current) return;
            setHasResultsWithoutImage(probe.titles.length > 0);
          } catch {
            // Stiller Fallback: die Probe ist nur ein Hinweis, kein harter Fehler.
          }
        }
      } catch (err) {
        if (requestId !== latestRequestRef.current) return;
        markLoaded();
        if (err instanceof TobisNotConfiguredError) {
          setPhase("notConfigured");
          return;
        }
        logApplicationFailure("tobis.titles");
        setPhase("error");
      }
    },
    [setPage, markLoaded],
  );

  // Naechste Seite anhaengen statt ersetzen. Der Katalog hat rund 545.000
  // Titel, die Seite laedt bewusst nur einen Ausschnitt.
  const loadMore = useCallback(async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await fetchTitles(filters, 100, cursor, sort);
      appendPage(page.titles, page.pageInfo.hasNextPage ? page.pageInfo.endCursor : null);
    } catch {
      logApplicationFailure("tobis.titles.more");
    } finally {
      setLoadingMore(false);
    }
  }, [cursor, filters, sort, loadingMore, appendPage]);

  // Auswahllisten einmal laden, sie aendern sich nur mit neuen Daten.
  useEffect(() => {
    void fetchFilterOptions()
      .then(setOptions)
      .catch(() => setOptions(null));
  }, []);

  // Entprellt, damit Tippen im Suchfeld nicht pro Zeichen eine Abfrage macht.
  // `loaded` ist die Bremse beim Zurueckkommen: der Stand steht schon, es gibt
  // nichts zu holen.
  useEffect(() => {
    if (loaded) return;
    const timer = window.setTimeout(() => void load(filters, sort, keywords, keywordMatchAll), 250);
    return () => window.clearTimeout(timer);
  }, [filters, sort, keywords, keywordMatchAll, loaded, load]);

  const hint =
    phase === "notConfigured"
      ? intl.formatMessage({
          id: "tobis.notConfigured",
          defaultMessage:
            "No database configured. Set TOBIS_DATABASE_URL in the BFF environment and run the migration.",
        })
      : phase === "error"
        ? intl.formatMessage({ id: "tobis.loadError", defaultMessage: "The overview could not be loaded." })
        : undefined;

  return (
    <TobisPageShell
      eyebrow={intl.formatMessage({ id: "tobis.eyebrow", defaultMessage: "Movie" })}
      title={intl.formatMessage({ id: "tobis.titles.heading", defaultMessage: "Titles" })}
      intro={intl.formatMessage({
        id: "tobis.titles.intro",
        defaultMessage:
          "Every title loaded so far, with the rating signals that carry a purchase decision. Votes matter as much as the rating: a high score on few votes says little.",
      })}
    >
      {hint ? (
        <Section title={intl.formatMessage({ id: "tobis.titles.heading", defaultMessage: "Titles" })}>
          <p className="text-sm text-ink/55">{hint}</p>
        </Section>
      ) : (
        // Filterleiste liegt bewusst ausserhalb der Section: bei 0 Treffern
        // ersetzt Section/NoBackend ihren Children-Block komplett (das ist
        // fuer einen echten Leerzustand richtig). Laege die Filterleiste
        // darin, wuerde sie bei "Filter matcht nichts" mit verschwinden und
        // der Nutzer haette keinen Weg mehr, den Filter zu loesen.
        <div className="space-y-5">
          <TobisTitleFilterBar
            filters={filters}
            onChange={setFilters}
            options={options}
            sort={sort}
            onSortChange={setSort}
            selectedKeywords={keywords}
            onKeywordsChange={setKeywords}
            keywordMatchAll={keywordMatchAll}
            onKeywordMatchAllChange={setKeywordMatchAll}
          />

          <Section
            title={intl.formatMessage({ id: "tobis.titles.heading", defaultMessage: "Titles" })}
            right={
              <div className="flex items-center gap-3">
                <SegmentedControl
                  value={view}
                  onChange={setView}
                  size="sm"
                  options={[
                    {
                      value: "grid",
                      label: intl.formatMessage({ id: "tobis.view.grid", defaultMessage: "Tiles" }),
                    },
                    {
                      value: "compact",
                      label: intl.formatMessage({ id: "tobis.view.compact", defaultMessage: "Compact" }),
                    },
                  ]}
                />
                <span className="text-[12px] text-ink/40">
                  {/* total kommt vom BFF nur auf der ersten Seite (countTitles) -
                      solange es gesetzt ist und mehr zaehlt als geladen, zeigen
                      wir "N von M", sonst die blanke Anzahl wie zuvor. */}
                  {total !== null && total > titles.length
                    ? intl.formatMessage(
                        { id: "tobis.titles.countOfTotal", defaultMessage: "{count} of {total} titles loaded" },
                        { count: intl.formatNumber(titles.length), total: intl.formatNumber(total) },
                      )
                    : intl.formatMessage(
                        { id: "tobis.titles.count", defaultMessage: "{count} titles" },
                        { count: intl.formatNumber(titles.length) },
                      )}
                </span>
              </div>
            }
            empty={phase === "ready" && titles.length === 0 && !hasResultsWithoutImage}
            emptyLabel={intl.formatMessage({
              id: "tobis.titles.empty",
              defaultMessage: "No titles loaded yet.",
            })}
            emptyHint={intl.formatMessage({
              id: "tobis.titles.emptyHint",
              defaultMessage: "Start a fetch under Sources.",
            })}
          >
            {phase === "ready" && titles.length === 0 && hasResultsWithoutImage ? (
              <div className="rounded-md bg-ink/[0.02] px-4 py-6 text-center ring-1 ring-ink/15">
                <div className="text-[12px] text-ink/55">
                  {intl.formatMessage({
                    id: "tobis.titles.emptyNoImage",
                    defaultMessage: "No titles with a cover image match these filters.",
                  })}
                </div>
                <div className="mt-1 text-[11px] text-ink/40">
                  {intl.formatMessage({
                    id: "tobis.titles.emptyNoImageHint",
                    defaultMessage: "There are matches without a cover image.",
                  })}
                </div>
                <div className="mt-3 flex justify-center">
                  <Button variant="secondary" onClick={() => setFilters({ ...filters, hasImage: false })}>
                    {intl.formatMessage({
                      id: "tobis.titles.removeImageFilter",
                      defaultMessage: "Remove cover filter",
                    })}
                  </Button>
                </div>
              </div>
            ) : phase === "loading" ? (
              // Eigener Zwischenzustand statt des NoBackend-Leertexts: der
              // Store leert `titles` schon beim Tippen, bevor die Antwort da
              // ist - ohne diesen Zweig sieht eine laufende Suche identisch
              // zu "0 Treffer" aus.
              <div
                role="status"
                aria-live="polite"
                className="flex items-center justify-center gap-2 rounded-md bg-ink/[0.02] px-4 py-6 ring-1 ring-ink/15"
              >
                <span
                  aria-hidden
                  className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent text-ink/40"
                />
                <span className="text-[12px] text-ink/55">
                  {intl.formatMessage({
                    id: "tobis.titles.loading",
                    defaultMessage: "Searching the database…",
                  })}
                </span>
              </div>
            ) : (
              <div className="space-y-5">
                {view === "grid" ? (
                  // Kachelraster im Zuschnitt von SHARE (ShareMinePage).
                  <div className="grid grid-cols-1 gap-4 @sm:grid-cols-2 @lg:grid-cols-4">
                    {titles.map((t, i) => (
                      <Link
                        key={t.id}
                        to={`/tobis/titles/${t.id}`}
                        className="block transition hover:-translate-y-0.5"
                      >
                        <TobisTitleCard title={t} index={i} />
                      </Link>
                    ))}
                  </div>
                ) : (
                  // Kompakt: kleines Poster statt grosser Kachel, damit sich ein
                  // Bestand von hunderten Titeln zuegig durchscrollen laesst.
                  <div className="grid grid-cols-1 gap-2 @lg:grid-cols-2">
                    {titles.map((t, i) => (
                      <TobisTitleRow key={t.id} title={t} index={i} />
                    ))}
                  </div>
                )}

                {cursor && (
                  <div className="flex justify-center">
                    <Button variant="secondary" onClick={() => void loadMore()} disabled={loadingMore}>
                      {loadingMore
                        ? intl.formatMessage({
                            id: "tobis.titles.loadingMore",
                            defaultMessage: "Loading…",
                          })
                        : intl.formatMessage({
                            id: "tobis.titles.loadMore",
                            defaultMessage: "Load more",
                          })}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </Section>
        </div>
      )}
    </TobisPageShell>
  );
}
